import { FC, useEffect, useMemo, useState } from "react";
import { useAppContext } from "../../../../context/app.context";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import cargoBreadcrumbsHelper from "../../common/breadcrumbs/cargo-breadcrumbs.helper";
import useCargoCargoTaxiContractListingFiltersDao from "./filter/use-cargo-cargo-taxi-contract-listing-filters.dao";
import CargoCargoTaxiContractListingSortKey from "./common/types/cargo-cargo-taxi-contract-listing-sort-key";
import CargoCargoTaxiContractListingFilter from "./common/types/cargo-cargo-taxi-contract-listing-filter";
import useClientSidePagination from "../../../../common/hooks/use-client-side-pagination";
import CargoCargoTaxiContractListing from "./common/types/cargo-cargo-taxi-contract-listing-item";
import cargoCargoTaxiContractListingItemFactory from "./common/cargo-cargo-taxi-contract-listing-item.factory";
import useContractsCargoTaxiList from "../../../../common/services/contracts/cargo-taxi/list/use-contracts-cargo-company-list";
import ContractsCargoTaxiListParams from "../../../../common/services/contracts/cargo-taxi/list/contracts-cargo-taxi-list-params";
import cargoTaxiContractListingLoadParamsFactory from "./common/cargo-cargo-taxi-contract-listing-load-params.factory";
import useAbort from "../../../../common/hooks/use-abort";
import cargoTranslationsHelper from "../../../../languages/cargo-translations.helper";
import useIsComponentMounted from "../../../../common/hooks/use-is-component-mounted";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import CargoCargoTaxiContractListingFilterComponent from "./filter/cargo-cargo-taxi-contract-listing-filter.component";
import CardComponent from "../../../../common/components/card/card.component";
import CargoCargoTaxiContractListingTableComponent from "./table/cargo-cargo-taxi-contract-listing-table.component";
import ListingPaginationComponent from "../../../../common/components/listing/pagination/listing-pagination.component";

type CargoCompanyContractListingCOmponentProps = {};

const CargoCompanyContractListingComponent: FC<
  CargoCompanyContractListingCOmponentProps
> = () => {
  const isComponentMounted = useIsComponentMounted();

  const { selectedAppLanguage, setBreadcrumbs } = useAppContext();

  const listingItemsAbort = useAbort();

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.cargoCargoTaxiContractListing);

  useEffect(() => {
    const breadcrumbs =
      cargoBreadcrumbsHelper.getCargoTaxiContractListingBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const filtersDao = useCargoCargoTaxiContractListingFiltersDao();

  const [sortKey, setSortKey] = useState<CargoCargoTaxiContractListingSortKey>(
    () =>
      filtersDao.getSortKey() ??
      CargoCargoTaxiContractListingSortKey.VALID_TO_ASC,
  );

  const [filters, setFilters] = useState<CargoCargoTaxiContractListingFilter[]>(
    () => filtersDao.getFilters(),
  );

  const cargoTaxiContractList = useContractsCargoTaxiList();

  const loadListingItems = async () => {
    const params: ContractsCargoTaxiListParams =
      cargoTaxiContractListingLoadParamsFactory.create(
        page,
        pageSize,
        filters,
        sortKey,
      );

    cargoTaxiContractList.load(params, listingItemsAbort.signal);
  };

  const listingItems: CargoCargoTaxiContractListing = useMemo(() => {
    return cargoCargoTaxiContractListingItemFactory.create(
      cargoTaxiContractList.data,
    );
  }, [cargoTaxiContractList.data]);

  const { pageData, page, pageSize, setPage, setPageSize, totalResults } =
    useClientSidePagination({
      data: listingItems.data,
      totalResults: listingItems.totalCount,
    });

  const saveFilters = () => {
    filtersDao.saveFilters(filters, sortKey, page, pageSize);
  };

  const translations =
    cargoTranslationsHelper.getCargoTaxiContractListingTranslations();

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    saveFilters();
  }, [filters, sortKey, page, pageSize]);

  useEffect(() => {
    window.scroll({ top: 0, behavior: "smooth" });
  }, [page]);

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    setPage(1);
  }, [pageSize, filters]);

  useEffect(() => {
    loadListingItems();

    return () => listingItemsAbort.revoke();
  }, [page, pageSize, filters, sortKey]);

  return (
    <>
      <HeadingComponent title={translations.header.headingText} />
      <CargoCargoTaxiContractListingFilterComponent
        filters={filters}
        sortKey={sortKey}
        onFiltersChange={setFilters}
        onSortKeyChange={setSortKey}
      />
      <CardComponent classNames={{ root: "mt-4" }}>
        <CargoCargoTaxiContractListingTableComponent
          listingItems={pageData}
          isError={false}
          isLoading={false}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={totalResults}
        />
      </CardComponent>
    </>
  );
};

export default CargoCompanyContractListingComponent;
